import { Campaign, getCampaign } from '@api/queries/getCampaign';
import { sanitizePhoneNumberTo10Digits } from '@lib/utils/formatPhone/sanitizePhoneNumberTo10Digits';
import TasksModule from '@modules/TasksModule';
import { tasksState } from '@modules/TasksModule/state';
import { isValidPhoneNumber } from '@pages/settings/phoneNumberUtils';

export const CACHE_TTL_MS = 8 * 60 * 60 * 1000; // 8 hours

export async function getCampaignByPhoneWithCache(
  tenantId: string,
  phoneNumber: string,
  getCampaign: ({
    tenantId,
    phoneNumber,
  }: {
    tenantId: string;
    phoneNumber: string;
  }) => Promise<Campaign | null>,
): Promise<Campaign | null> {
  try {
    const tenantCache = tasksState.phoneNumberToCampaign[tenantId] ?? {};
    const cacheEntry = tenantCache[phoneNumber];
    const now = Date.now();

    if (cacheEntry && now - cacheEntry.fetchedAt < CACHE_TTL_MS) {
      return cacheEntry.campaign;
    }

    const campaign = await getCampaign({ tenantId, phoneNumber });
    if (!tasksState.phoneNumberToCampaign[tenantId]) {
      tasksState.phoneNumberToCampaign[tenantId] = {};
    }
    tasksState.phoneNumberToCampaign[tenantId][phoneNumber] = {
      campaign,
      fetchedAt: now,
    };
    return campaign;
  } catch (error) {
    console.error(
      'Error fetching campaign by phone with cache:',
      error,
      tenantId,
      phoneNumber,
    );
    throw error;
  }
}

export async function fetchCampaignForTheCall({
  tenantId,
  toPhoneNumber,
  taskId,
}: {
  tenantId: string;
  toPhoneNumber: string;
  taskId: string;
}) {
  try {
    const task = TasksModule.getTask(taskId);
    if (!task || task?.attributes?.campaign?.name) {
      // campaign already exists on the task
      return;
    }
    if (!(+tenantId > 0)) {
      // tenantId should be a number > 0
      throw new Error(
        `Invalid tenantId in fetchCampaignForTheCall, tenantId ${tenantId}`,
      );
    }

    if (!isValidPhoneNumber(toPhoneNumber)) {
      throw new Error(
        `Invalid phone number in fetchCampaignForTheCall, phoneNumber ${toPhoneNumber}`,
      );
    }
    const phoneNumber = sanitizePhoneNumberTo10Digits(toPhoneNumber);
    const campaign = await getCampaignByPhoneWithCache(
      tenantId,
      phoneNumber,
      getCampaign,
    );

    if (campaign?.name) {
      TasksModule.setTaskAttribute(task.id, 'campaign', {
        id: campaign.id,
        name: campaign.name,
      });
    }
  } catch (error) {
    console.error(
      'Error in fetchCampaignForTheCall:',
      error,
      taskId,
      tenantId,
      toPhoneNumber,
    );
  }
}
